import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../services/api';
import Icon from '../../components/AppIcon';
import DashboardHeader from '../../components/ui/DashboardHeader';
import DynamicSidebar from '../../components/ui/DynamicSidebar';

const ACTION_TYPES = [
  { value: '', label: 'All Actions' },
  { value: 'role_assigned', label: 'Role Assigned' },
  { value: 'role_removed', label: 'Role Removed' },
  { value: 'permission_changed', label: 'Permission Changed' },
  { value: 'user_created', label: 'User Created' },
  { value: 'user_updated', label: 'User Updated' },
  { value: 'login', label: 'Login' },
  { value: 'logout', label: 'Logout' },
  { value: 'password_reset', label: 'Password Reset' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'success', label: 'Success' },
  { value: 'failed', label: 'Failed' },
];

const formatDateTime = (timestamp) => {
  if (!timestamp) return '—';
  try {
    const date = new Date(timestamp);
    return date?.toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch {
    return timestamp;
  }
};

const StatusBadge = ({ status }) => {
  const isSuccess = status?.toLowerCase() === 'success';
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
      isSuccess ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
    }`}>
      <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
        isSuccess ? 'bg-success' : 'bg-error'
      }`} />
      {isSuccess ? 'Success' : 'Failed'}
    </span>
  );
};

const AuditLogsPage = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [actionType, setActionType] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const handleMenuToggle = () => {
    setSidebarCollapsed(prev => !prev);
  };

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, size: pageSize };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (userSearch) params.user_search = userSearch;
      if (actionType) params.action = actionType;
      if (statusFilter) params.status = statusFilter;

      const response = await apiService?.getAuditLogs(params);
      const data = response?.data;
      const details = data?.details || [];
      setLogs(details);
      setTotalCount(data?.total || details?.length || 0);
      setTotalPages(data?.total_pages || Math.ceil((data?.total || details?.length) / pageSize) || 1);
    } catch (err) {
      setError('Failed to load audit logs. Please try again.');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, startDate, endDate, userSearch, actionType, statusFilter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleSearch = (e) => {
    e?.preventDefault();
    setPage(1);
    fetchLogs();
  };

  const handleReset = () => {
    setStartDate('');
    setEndDate('');
    setUserSearch('');
    setActionType('');
    setStatusFilter('');
    setPage(1);
  };

  const handleExport = () => {
    if (!logs?.length) return;
    const headers = ['ID', 'User ID', 'First Name', 'Last Name', 'Email', 'Action', 'Role Before', 'Role After', 'Timestamp', 'Status'];
    const rows = logs?.map(log => [
      log?.id || '',
      log?.user?.user_id || '',
      log?.user?.first_name || '',
      log?.user?.last_name || '',
      log?.user?.email || '',
      log?.action || '',
      log?.role_changes?.before || '',
      log?.role_changes?.after || '',
      log?.timestamp || '',
      log?.status || '',
    ]);
    const csvContent = [headers, ...rows]?.map(r => r?.map(v => `"${String(v)?.replace(/"/g, '""')}"`)?.join(','))?.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `audit-logs-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    link?.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-background">
      <DashboardHeader onMenuToggle={handleMenuToggle} />
      <DynamicSidebar isCollapsed={sidebarCollapsed} onToggle={handleMenuToggle} />
      <main className={`transition-all duration-300 pt-16 ${
        sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
      }`}>
        <div className="p-6">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-text-primary">Audit Logs</h1>
              <p className="text-sm text-text-secondary mt-1">Track all admin actions and system events for accountability</p>
            </div>
            <button
              onClick={handleExport}
              disabled={!logs?.length}
              className="flex items-center space-x-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-smooth"
            >
              <Icon name="Download" size={16} />
              <span>Export CSV</span>
            </button>
          </div>

          {/* Filters */}
          <div className="bg-surface border border-border rounded-xl p-4 mb-6">
            <form onSubmit={handleSearch}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                {/* Date Range */}
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1">Start Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={e => setStartDate(e?.target?.value)}
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1">End Date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={e => setEndDate(e?.target?.value)}
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                {/* User Search */}
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1">Search User</label>
                  <div className="relative">
                    <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
                    <input
                      type="text"
                      value={userSearch}
                      onChange={e => setUserSearch(e?.target?.value)}
                      placeholder="Name or email..."
                      className="w-full pl-8 pr-3 py-2 text-sm border border-border rounded-lg bg-background text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                  </div>
                </div>
                {/* Action Type */}
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1">Action Type</label>
                  <select
                    value={actionType}
                    onChange={e => setActionType(e?.target?.value)}
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                  >
                    {ACTION_TYPES?.map(opt => (
                      <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
                    ))}
                  </select>
                </div>
                {/* Status */}
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1">Status</label>
                  <select
                    value={statusFilter}
                    onChange={e => setStatusFilter(e?.target?.value)}
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                  >
                    {STATUS_OPTIONS?.map(opt => (
                      <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex items-center space-x-3 mt-4">
                <button
                  type="submit"
                  className="flex items-center space-x-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-smooth"
                >
                  <Icon name="Filter" size={14} />
                  <span>Apply Filters</span>
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  className="flex items-center space-x-2 px-4 py-2 border border-border text-text-secondary rounded-lg text-sm font-medium hover:bg-muted transition-smooth"
                >
                  <Icon name="RotateCcw" size={14} />
                  <span>Reset</span>
                </button>
              </div>
            </form>
          </div>

          {/* Table */}
          <div className="bg-surface border border-border rounded-xl overflow-hidden">
            {/* Table Header Info */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <span className="text-sm text-text-secondary">
                {loading ? 'Loading...' : `${totalCount} record${totalCount !== 1 ? 's' : ''} found`}
              </span>
              <button onClick={fetchLogs} className="flex items-center space-x-1 text-sm text-text-secondary hover:text-text-primary transition-smooth">
                <Icon name="RefreshCw" size={14} />
                <span>Refresh</span>
              </button>
            </div>

            {error && (
              <div className="flex items-center space-x-2 px-4 py-3 bg-error/5 border-b border-error/20 text-error text-sm">
                <Icon name="AlertCircle" size={16} />
                <span>{error}</span>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide">User</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide">Action</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide">Role Changes</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide">Timestamp</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {loading ? (
                    Array.from({ length: 5 })?.map((_, i) => (
                      <tr key={i}>
                        {Array.from({ length: 5 })?.map((__, j) => (
                          <td key={j} className="px-4 py-3">
                            <div className="h-4 bg-muted rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : logs?.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-12 text-center">
                        <div className="flex flex-col items-center space-y-2 text-text-secondary">
                          <Icon name="ClipboardList" size={32} className="opacity-40" />
                          <p className="text-sm">No audit logs found</p>
                          <p className="text-xs">Try adjusting your filters or date range</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    logs?.map((log) => (
                      <tr key={log?.id} className="hover:bg-muted/30 transition-smooth">
                        {/* User */}
                        <td className="px-4 py-3">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-xs font-semibold text-primary">
                                {(log?.user?.first_name?.[0] || '') + (log?.user?.last_name?.[0] || '') || '?'}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-text-primary truncate">
                                {log?.user?.first_name} {log?.user?.last_name}
                              </p>
                              <p className="text-xs text-text-secondary truncate">{log?.user?.email}</p>
                              <p className="text-xs text-text-secondary font-mono">{log?.user?.user_id}</p>
                            </div>
                          </div>
                        </td>
                        {/* Action */}
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-primary/10 text-primary">
                            {log?.action || '—'}
                          </span>
                        </td>
                        {/* Role Changes */}
                        <td className="px-4 py-3">
                          {log?.role_changes?.before || log?.role_changes?.after ? (
                            <div className="flex items-center space-x-2 text-xs">
                              {log?.role_changes?.before && (
                                <span className="px-2 py-0.5 rounded bg-error/10 text-error font-medium">
                                  {log?.role_changes?.before}
                                </span>
                              )}
                              {log?.role_changes?.before && log?.role_changes?.after && (
                                <Icon name="ArrowRight" size={12} className="text-text-secondary flex-shrink-0" />
                              )}
                              {log?.role_changes?.after && (
                                <span className="px-2 py-0.5 rounded bg-success/10 text-success font-medium">
                                  {log?.role_changes?.after}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-text-secondary">—</span>
                          )}
                        </td>
                        {/* Timestamp */}
                        <td className="px-4 py-3">
                          <span className="text-sm text-text-primary whitespace-nowrap">{formatDateTime(log?.timestamp)}</span>
                        </td>
                        {/* Status */}
                        <td className="px-4 py-3">
                          <StatusBadge status={log?.status} />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {!loading && logs?.length > 0 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                <span className="text-sm text-text-secondary">
                  Page {page} of {totalPages}
                </span>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="flex items-center space-x-1 px-3 py-1.5 text-sm border border-border rounded-lg text-text-secondary hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-smooth"
                  >
                    <Icon name="ChevronLeft" size={14} />
                    <span>Prev</span>
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
                    return pageNum <= totalPages ? (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        className={`w-8 h-8 text-sm rounded-lg font-medium transition-smooth ${
                          pageNum === page
                            ? 'bg-primary text-primary-foreground'
                            : 'border border-border text-text-secondary hover:bg-muted'
                        }`}
                      >
                        {pageNum}
                      </button>
                    ) : null;
                  })}
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="flex items-center space-x-1 px-3 py-1.5 text-sm border border-border rounded-lg text-text-secondary hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-smooth"
                  >
                    <span>Next</span>
                    <Icon name="ChevronRight" size={14} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AuditLogsPage;
